import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import './countrySelector.less'
import { countries } from '../../constants/countries'
import { useViewport } from '../../hooks/responsive'
export interface Option {
  code: string
  name: string
}
interface Props {
  onChange: (value: string) => void
  onSelect: (value: string) => void
  onFocus: () => void
  httpRef: (el: any) => void
  value: string
  isActive: boolean
  autoFocus: boolean
  onClick?: () => void
  inputPlaceholder?: string
  countryCode?: string
  setIsCountrySelect: (value: boolean) => void
  isCountrySelect?: boolean
  setCountry?: any
  setIsError?: any
  isError?: boolean
  currentInput?: any
  clearInputField?: any
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const CountrySelector: React.FC<Props> = ({
  httpRef,
  value,
  onChange,
  onSelect,
  onFocus,
  isActive,
  autoFocus,
  onClick,
  inputPlaceholder = '',
  countryCode,
  setIsCountrySelect,
  isCountrySelect,
  setCountry,
  setIsError,
  isError,
  currentInput,
  clearInputField,
  onKeyDown,
}: Props) => {
  const { t } = useTranslation()
  const [filteredOptions, setOptions] = useState(countries)
  const [expanded, setExpended] = useState(false)
  const [translatedCountries, setTranslatedCountries] = useState()
  const [isClearNeed, setIsClearNeed] = useState(true)
  const {isMobile} = useViewport()
  
  const countriesTranslating = useCallback(() => {
    return countries.map((item) => ({
      code: item.code,
      name: t(`countries:${item.code}`) && t(`countries:${item.code}`).toUpperCase(),
    }))
  }, [t])

  const onOptionSelect = (option: Option) => {
    onChange(option.name)
    onSelect(option.code)
    setExpended(false)
    setIsClearNeed(true)
  }

  const filteredWithNewValue = useCallback(
    (value) => {
      const searchTerm = value.toUpperCase()
      const filtered =
        translatedCountries &&
        translatedCountries.filter((item: Option) => item.name && item.name.indexOf(searchTerm) === 0)
      setOptions([...filtered].slice(0, 3))
      isActive && !isCountrySelect && setExpended(true)
      isActive && !isCountrySelect && setIsClearNeed(false)
      isActive && !isCountrySelect && setIsError(!filtered.length)
      !isCountrySelect && isError && setIsClearNeed(true)
      isCountrySelect && setIsClearNeed(true)
    },
    [isActive, isCountrySelect, isError, setIsError, translatedCountries],
  )

  useEffect(() => {
    if (value) {
      filteredWithNewValue(value)
    } else {
      setExpended(false)
      setIsClearNeed(false)
      setIsError(false)
    }
    setCountry(value)
  }, [value, filteredWithNewValue, setIsError, setCountry])

  const translatedCountriesFiltered = useCallback(() => {
    setExpended(false)
    const translatedCountriesFirst = countriesTranslating()
    setTranslatedCountries(translatedCountriesFirst)
    const filtered = translatedCountriesFirst.find(
      (item: Option) => item.code === countryCode && countryCode.toUpperCase(),
    )
    filtered && setCountry(filtered.name)
    filtered && setIsCountrySelect(true)
  }, [countriesTranslating, countryCode, setCountry, setIsCountrySelect])

  useEffect(() => {
    translatedCountriesFiltered()
  }, [translatedCountriesFiltered])

  useEffect(() => {
    setTranslatedCountries(countriesTranslating())
  }, [countriesTranslating])

  useEffect(() => {
    setIsClearNeed(isError || false)
  }, [isError])

  return (
    <div className="selection-list">
      {isClearNeed && (
        <div className={`${isMobile ? 'clear-input-mobile' : 'clear-input'}`}>
          <button className="clear-input-button" onClick={() => clearInputField(currentInput)} tabIndex={-1}>
            &#xd7;
          </button>
        </div>
      )}
      <input
        tabIndex={0}
        autoComplete='off'
        spellCheck={false}
        placeholder={inputPlaceholder}
        ref={httpRef}
        className={`${isMobile ? 'input-field-mobile': 'input-field'}${isError ? ' error-input': ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        autoFocus={autoFocus}
        onClick={onClick}
        onKeyDown={onKeyDown}
      />
      {expanded && isActive && (
        <div className={isMobile ? 'selection-list-options-mobile': 'selection-list-options'}>
          {filteredOptions.map((item) => (
            <div key={item.name} onClick={() => onOptionSelect(item)} className="selection-list-option">
              {t(`countries:${item.code}`)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
export default CountrySelector
